
import {bindCallback, fromEventPattern, Observable, of} from "rxjs";

import {Injectable} from "@angular/core";
import OneSignal from "onesignal-cordova-plugin";
import {map} from "rxjs/operators";
import {Capacitor} from "@capacitor/core";
import {PushNotifications, PushNotificationSchema} from "@capacitor/push-notifications";
import {OpenedEvent} from "onesignal-cordova-plugin/types/Notification";
import {ChangeEvent} from "onesignal-cordova-plugin/types/Extras";
import {SubscriptionChange} from "onesignal-cordova-plugin/types/Subscription";
import {PushNotificationsService} from "./domain/push-notifications.service";
import {NotificationOpenedResult} from "./domain/model/notification-opened-result";
import {PushNotificationsRepository} from "./domain/repository/push-notifications-repository";
import {OneSignalPushNotificationMapper} from "./one-signal-push-notification.mapper";
import {DeviceDAO} from "./data/device.dao";
import {DeviceWS} from "./data/device.ws";
import {PushNotificationsDataRepository} from "./data/repository/push-notifications-data-repository";



@Injectable()
export class OneSignalPushNotificationsService implements PushNotificationsService {

  protected pushNotificationsRepository?: PushNotificationsRepository;

  initWithRepository(appId: string, pushNotificationsRepository: PushNotificationsRepository): void {
    this.pushNotificationsRepository = pushNotificationsRepository;
    OneSignal.setLogLevel(0, 0);
    OneSignal.unsubscribeWhenNotificationsAreDisabled(true);
    this.activateNotificationsInForeground();
    OneSignal.setAppId(appId);
  }

  init(appId: string, deviceDAO: DeviceDAO, deviceWS: DeviceWS): void {
    this.initWithRepository(appId, new PushNotificationsDataRepository(deviceDAO, deviceWS));
  }

  /**
   * Returns an infinite observable that emits a additionalData field of the notification when a notification has
   * been opened
   */
  get onNotificationOpen$(): Observable<NotificationOpenedResult> {
    return fromEventPattern<OpenedEvent>(OneSignal.setNotificationOpenedHandler)
      .pipe(
        map(notificationOpenedEvent => OneSignalPushNotificationMapper.deserialize(notificationOpenedEvent))
      )
  }

  /**
   * Returns an infinite observable that emits the deviceId if any when a subscription has been changed
   */
  get onSubscriptionChange$(): Observable<string | undefined> {
    return fromEventPattern<ChangeEvent<SubscriptionChange>>(OneSignal.addSubscriptionObserver)
      .pipe(map(value => value.to.userId))
  }


  /**
   * Needed for notifications to appear on iOS when application is open
   * Does not work because capacitor conflicts with cordova https://github.com/OneSignal/OneSignal-Cordova-SDK/issues/781
   */
  activateNotificationsInForeground() {

    // When notification received
    PushNotifications.addListener('pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        // this is needed to force the device to show the notifications when app in foreground
      })

    OneSignal.setNotificationWillShowInForegroundHandler(
      // Resend notification to app
      notificationReceived=> notificationReceived.complete(notificationReceived.getNotification()))
  }

  /**
   * Ask user for permission to use notification and returns the response as a boolean observable then completes
   */
  requestPermissions(): Observable<boolean> {
    if(Capacitor.getPlatform() === 'android')
      // no need to ask permissions on android
      return of(true)
    return bindCallback(OneSignal.promptForPushNotificationsWithUserResponse)()
  }

  /**
   * Tries to sent pending devices to a remote host, starts listening device identifier changes, starts listening
   * user notification clicks and finally enable receiving push notifications
   */
  enableNotifications(): void {
    // Retry sending not sent identifiers
    this.pushNotificationsRepository?.createPendingDevice();

    // Start listening changes to receive new identifiers
    OneSignal.addSubscriptionObserver(changeEvent => {
      const newUserId = changeEvent.to.userId;
      const oldUserId = changeEvent.from.userId;

      // Check if identifier has been changed
      if (newUserId != null && oldUserId !== newUserId) {
        this.pushNotificationsRepository?.createDeviceId(newUserId)
      }

    })
    OneSignal.disablePush(false);
  }

  /**
   * Clear all notifications and disable notifications
   */
  disableNotifications(): void {
    OneSignal.clearOneSignalNotifications()
    OneSignal.disablePush(true)
  }

}
